/**
 * VolumeIIBoilerplate — v3.18.14 (Strategy Phase 3A)
 *
 * Two-pane workspace for Volume II / Business Volume reusable boilerplate:
 *   LEFT  – Library: consultancy-scoped templates with category filter,
 *           one-click "Pull into pursuit" CTA, and a one-time "Seed
 *           Volume II Pack" button when the library is empty.
 *   RIGHT – Drafts in this pursuit: pulled-in templates ready to edit
 *           (title, body, variable values, status DRAFT/REVIEWED/FINAL).
 *
 * All endpoints under /api/strategy/boilerplate-library and
 * /api/pursuits/{id}/strategy/boilerplate-drafts. Templates are
 * consultancy-scoped (Reusable Asset Library pattern), drafts are
 * pursuit-scoped + decoupled (editing a draft never touches the source
 * template).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Loader2, BookOpen, Plus, Sparkles, Edit, Save, Trash2,
  X, FileText, CheckCircle2, Library, Wand2,
} from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DRAFT_STATUS_META = {
  DRAFT:    { label: 'Draft',    tone: 'bg-slate-100 text-slate-700' },
  REVIEWED: { label: 'Reviewed', tone: 'bg-amber-100 text-amber-700' },
  FINAL:    { label: 'Final',    tone: 'bg-emerald-100 text-emerald-700' },
};

// v3.18.15 — Supported volume tabs. Adding a new volume here AND the
// matching backend seed pack is all it takes to extend the library.
const VOLUME_TABS = [
  { id: 'II', label: 'Volume II', sub: 'Business Volume' },
  { id: 'IV', label: 'Volume IV', sub: 'Cost Volume' },
];

// Substitute {{var}} tokens in a body string with their filled values.
// Unfilled tokens are highlighted so the user can spot what's left.
function renderBody(body, filled) {
  return (body || '').replace(/\{\{\s*([a-zA-Z0-9_-]+)\s*\}\}/g, (_, key) => {
    const v = filled?.[key];
    return v ? v : `{{${key}}}`;
  });
}

export default function VolumeIIBoilerplate({ pursuitId }) {
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [pulling, setPulling] = useState(null); // template_id being pulled
  const [seeding, setSeeding] = useState(false);
  const [editingDraft, setEditingDraft] = useState(null); // draft.id
  const [draftEdit, setDraftEdit] = useState({});
  const [saving, setSaving] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('All');
  // v3.18.15 — Active volume tab. Filters both the Library list and the
  // Drafts list. Defaults to Volume II (Business Volume).
  const [activeVolume, setActiveVolume] = useState('II');

  const fetchAll = useCallback(async () => {
    if (!pursuitId) return;
    setLoading(true);
    try {
      const [tplRes, drRes] = await Promise.all([
        fetch(`${API_URL}/api/strategy/boilerplate-library`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/pursuits/${pursuitId}/strategy/boilerplate-drafts`, { headers: getAuthHeaders() }),
      ]);
      if (tplRes.ok) setTemplates((await tplRes.json()).templates || []);
      if (drRes.ok) setDrafts((await drRes.json()).drafts || []);
    } finally {
      setLoading(false);
    }
  }, [pursuitId, getAuthHeaders]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const categories = useMemo(() => {
    const set = new Set(['All']);
    templates
      .filter((t) => (t.volume || 'II') === activeVolume)
      .forEach((t) => set.add(t.category || 'General'));
    return Array.from(set);
  }, [templates, activeVolume]);

  const filteredTemplates = useMemo(() => {
    const byVolume = templates.filter((t) => (t.volume || 'II') === activeVolume);
    if (categoryFilter === 'All') return byVolume;
    return byVolume.filter((t) => (t.category || 'General') === categoryFilter);
  }, [templates, categoryFilter, activeVolume]);

  // Drafts in the current volume
  const filteredDrafts = useMemo(
    () => drafts.filter((d) => (d.volume || 'II') === activeVolume),
    [drafts, activeVolume],
  );

  // Re-fetch nothing — but reset category filter when switching volumes so
  // a category from one volume doesn't hide the new volume's templates.
  useEffect(() => {
    setCategoryFilter('All');
  }, [activeVolume]);

  const draftBySourceId = useMemo(() => {
    const map = {};
    drafts.forEach((d) => { if (d.source_template_id) map[d.source_template_id] = d; });
    return map;
  }, [drafts]);

  const seedLibrary = async () => {
    setSeeding(true);
    try {
      const res = await fetch(
        `${API_URL}/api/strategy/boilerplate-library/seed?volume=${activeVolume}`,
        { method: 'POST', headers: getAuthHeaders() },
      );
      if (!res.ok) throw new Error('Seed failed');
      const body = await res.json();
      const r = (body.results && body.results[0]) || {};
      toast.success(`Seeded ${r.inserted || 0} new ${r.name || `Volume ${activeVolume}`} templates`);
      fetchAll();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSeeding(false);
    }
  };

  const pullTemplate = async (template) => {
    setPulling(template.id);
    try {
      const res = await fetch(
        `${API_URL}/api/pursuits/${pursuitId}/strategy/boilerplate-drafts/from-template/${template.id}`,
        { method: 'POST', headers: getAuthHeaders() },
      );
      if (!res.ok) throw new Error('Pull failed');
      const body = await res.json();
      toast.success(body.created ? `Pulled "${template.title}" into this pursuit` : `Already in this pursuit`);
      fetchAll();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setPulling(null);
    }
  };

  const startEdit = (draft) => {
    setEditingDraft(draft.id);
    setDraftEdit({
      title: draft.title || '',
      body: draft.body || '',
      variables_filled: { ...(draft.variables_filled || {}) },
      status: draft.status || 'DRAFT',
    });
  };

  const cancelEdit = () => { setEditingDraft(null); setDraftEdit({}); };

  const saveDraft = async (draftId) => {
    setSaving(true);
    try {
      const res = await fetch(
        `${API_URL}/api/pursuits/${pursuitId}/strategy/boilerplate-drafts/${draftId}`,
        {
          method: 'PATCH',
          headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({
            title: draftEdit.title,
            body: draftEdit.body,
            variables_filled: draftEdit.variables_filled || {},
            status: draftEdit.status,
          }),
        },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.detail || `HTTP ${res.status}`);
      }
      toast.success('Draft saved');
      cancelEdit();
      fetchAll();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const deleteDraft = async (draft) => {
    if (!confirm(`Remove "${draft.title}" from this pursuit?`)) return;
    try {
      const res = await fetch(
        `${API_URL}/api/pursuits/${pursuitId}/strategy/boilerplate-drafts/${draft.id}`,
        { method: 'DELETE', headers: getAuthHeaders() },
      );
      if (!res.ok) throw new Error('Failed to delete');
      toast.success('Draft removed');
      fetchAll();
    } catch (e) {
      toast.error(e.message);
    }
  };

  // v3.18.16 — One-click auto-fill of common federal-profile variables
  // (company_name, cage_code, uei, business_size, hq location) from the
  // pursuit's client org. Never overwrites user-set values.
  const autofillFromClient = async (draft) => {
    try {
      const res = await fetch(
        `${API_URL}/api/pursuits/${pursuitId}/strategy/boilerplate-drafts/${draft.id}/autofill-from-client`,
        { method: 'POST', headers: getAuthHeaders() },
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.detail || `HTTP ${res.status}`);
      if (body.filled_count === 0) {
        toast.info(
          body.total_variables === 0
            ? 'No variables to fill on this draft.'
            : `No matching profile fields found. Edit the draft to fill ${body.skipped_unmapped.length} variables manually.`
        );
      } else {
        toast.success(
          `Filled ${body.filled_count} of ${body.total_variables} variables from client profile`
        );
      }
      fetchAll();
    } catch (e) {
      toast.error(e.message);
    }
  };

  if (loading) {
    return (
      <Card><CardContent className="py-10 text-center text-slate-500">
        <Loader2 className="w-5 h-5 animate-spin mx-auto" />
      </CardContent></Card>
    );
  }

  return (
    <Card data-testid="volume-ii-boilerplate">
      <CardContent className="p-0">
        {/* v3.18.15 — Volume selector tabs. Switching volumes filters
            BOTH the library and the drafts pane simultaneously. */}
        <div
          className="flex items-stretch border-b border-slate-200 bg-slate-50/60"
          role="tablist"
          aria-label="Proposal volume"
          data-testid="boilerplate-volume-tabs"
        >
          {VOLUME_TABS.map((tab) => {
            const isActive = activeVolume === tab.id;
            const count = templates.filter((t) => (t.volume || 'II') === tab.id).length;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => setActiveVolume(tab.id)}
                className={`flex-1 px-4 py-3 text-left border-r border-slate-200 last:border-r-0 transition-colors ${
                  isActive
                    ? 'bg-white text-slate-900 shadow-[inset_0_-2px_0_0_rgba(99,102,241,0.9)]'
                    : 'text-slate-600 hover:bg-white/60 hover:text-slate-900'
                }`}
                data-testid={`boilerplate-volume-tab-${tab.id}`}
              >
                <div className="text-sm font-semibold flex items-center gap-2">
                  {tab.label}
                  <Badge variant="secondary" className="text-[10px] font-normal">{count}</Badge>
                </div>
                <div className="text-[11px] text-slate-500">{tab.sub}</div>
              </button>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-slate-200">
          {/* ===================== LEFT — LIBRARY ===================== */}
          <div className="p-4" data-testid="boilerplate-library-pane">
            <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
              <div className="flex items-center gap-2">
                <Library className="w-5 h-5 text-indigo-600" />
                <h3 className="font-semibold text-slate-900">Library</h3>
                <Badge variant="secondary" className="text-xs">
                  {templates.filter((t) => (t.volume || 'II') === activeVolume).length}
                </Badge>
              </div>
              {templates.filter((t) => (t.volume || 'II') === activeVolume).length === 0 && (
                <Button
                  size="sm"
                  onClick={seedLibrary}
                  disabled={seeding}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white"
                  data-testid="boilerplate-seed-btn"
                >
                  {seeding ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
                  Seed Volume {activeVolume} Pack
                </Button>
              )}
            </div>

            {templates.filter((t) => (t.volume || 'II') === activeVolume).length > 0 && (
              <div className="flex flex-wrap gap-1 mb-3" data-testid="boilerplate-category-filter">
                {categories.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setCategoryFilter(c)}
                    className={`text-[11px] px-2 py-0.5 rounded border ${
                      categoryFilter === c
                        ? 'bg-indigo-100 border-indigo-300 text-indigo-700 font-medium'
                        : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            )}

            {templates.filter((t) => (t.volume || 'II') === activeVolume).length === 0 ? (
              <div className="py-10 text-center text-slate-500">
                <BookOpen className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                <p className="text-sm">
                  No Volume {activeVolume} boilerplate yet. Click{' '}
                  <strong>Seed Volume {activeVolume} Pack</strong> above to drop in{' '}
                  {activeVolume === 'II'
                    ? '9 Federal-grade Business Volume sections (Corporate Experience, Management Approach, Financial Responsibility, FAR Clauses, Small Biz Plan, and more).'
                    : '10 Federal-grade Cost Volume narratives (BOE Methodology, Labor Rates, Indirect Rates, Escalation, ODCs, Travel, Subcontracts, Cost Realism, and more).'}
                </p>
              </div>
            ) : (
              <ul className="space-y-2" data-testid="boilerplate-template-list">
                {filteredTemplates.map((t) => {
                  const pulled = !!draftBySourceId[t.id];
                  return (
                    <li
                      key={t.id}
                      className="border border-slate-200 rounded-md p-3 hover:bg-slate-50"
                      data-testid={`boilerplate-template-${t.section_id}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="font-medium text-sm text-slate-900 truncate">{t.title}</div>
                          <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                            <Badge variant="outline" className="text-[10px]">{t.category || 'General'}</Badge>
                            {t.is_seed && (
                              <Badge variant="outline" className="text-[10px] border-indigo-200 text-indigo-700 bg-indigo-50">
                                Seed
                              </Badge>
                            )}
                            <span className="text-[10px] text-slate-400 ml-1">
                              {(t.variables || []).length} variable{(t.variables || []).length === 1 ? '' : 's'}
                            </span>
                          </div>
                        </div>
                        {pulled ? (
                          <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-[11px]">
                            <CheckCircle2 className="w-3 h-3 mr-1" /> Pulled
                          </Badge>
                        ) : (
                          <Button
                            size="sm" variant="outline"
                            disabled={pulling === t.id}
                            onClick={() => pullTemplate(t)}
                            className="h-7 px-2 text-xs"
                            data-testid={`boilerplate-pull-${t.section_id}`}
                          >
                            {pulling === t.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5 mr-1" />}
                            Pull
                          </Button>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* ===================== RIGHT — DRAFTS ===================== */}
          <div className="p-4" data-testid="boilerplate-drafts-pane">
            <div className="flex items-center gap-2 mb-3">
              <FileText className="w-5 h-5 text-emerald-600" />
              <h3 className="font-semibold text-slate-900">In this pursuit</h3>
              <Badge variant="secondary" className="text-xs">{filteredDrafts.length}</Badge>
            </div>

            {filteredDrafts.length === 0 ? (
              <div className="py-10 text-center text-slate-500">
                <FileText className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                <p className="text-sm">
                  No Volume {activeVolume} drafts yet. Pick a template on the left and click <strong>Pull</strong>.
                </p>
              </div>
            ) : (
              <ul className="space-y-3" data-testid="boilerplate-draft-list">
                {filteredDrafts.map((d) => {
                  const isEditing = editingDraft === d.id;
                  const statusMeta = DRAFT_STATUS_META[d.status] || DRAFT_STATUS_META.DRAFT;
                  const unfilledCount = (d.body || '').match(/\{\{\s*[a-zA-Z0-9_-]+\s*\}\}/g)?.filter((m) => {
                    const key = m.replace(/[{}\s]/g, '');
                    return !(d.variables_filled || {})[key];
                  }).length || 0;

                  if (isEditing) {
                    return (
                      <li
                        key={d.id}
                        className="border border-violet-200 bg-violet-50/30 rounded-md p-3 space-y-2"
                        data-testid={`boilerplate-draft-${d.id}-editing`}
                      >
                        <Input
                          value={draftEdit.title}
                          onChange={(e) => setDraftEdit({ ...draftEdit, title: e.target.value })}
                          className="font-medium text-sm h-8"
                          data-testid={`boilerplate-draft-title-${d.id}`}
                        />
                        {/* Variable substitution helper */}
                        {(d.body || '').match(/\{\{\s*([a-zA-Z0-9_-]+)\s*\}\}/g) && (
                          <div className="border border-slate-200 bg-white rounded p-2 space-y-1">
                            <div className="text-[11px] font-semibold text-slate-600 mb-1">Variables</div>
                            {Array.from(new Set(
                              ((d.body || '').match(/\{\{\s*([a-zA-Z0-9_-]+)\s*\}\}/g) || [])
                                .map((m) => m.replace(/[{}\s]/g, ''))
                            )).map((key) => (
                              <div key={key} className="flex items-center gap-2">
                                <span className="text-[11px] font-mono text-slate-500 w-40 truncate" title={key}>
                                  {key}
                                </span>
                                <Input
                                  value={draftEdit.variables_filled?.[key] || ''}
                                  onChange={(e) => setDraftEdit({
                                    ...draftEdit,
                                    variables_filled: { ...(draftEdit.variables_filled || {}), [key]: e.target.value },
                                  })}
                                  className="h-7 text-xs flex-1"
                                  placeholder={`Value for {{${key}}}`}
                                  data-testid={`boilerplate-var-${d.id}-${key}`}
                                />
                              </div>
                            ))}
                          </div>
                        )}
                        <Textarea
                          value={draftEdit.body}
                          onChange={(e) => setDraftEdit({ ...draftEdit, body: e.target.value })}
                          rows={10}
                          className="font-mono text-xs"
                          data-testid={`boilerplate-draft-body-${d.id}`}
                        />
                        <div className="flex items-center justify-between gap-2">
                          <select
                            value={draftEdit.status}
                            onChange={(e) => setDraftEdit({ ...draftEdit, status: e.target.value })}
                            className="h-8 text-xs border border-slate-200 rounded px-2 bg-white"
                            data-testid={`boilerplate-draft-status-${d.id}`}
                          >
                            {Object.entries(DRAFT_STATUS_META).map(([v, m]) => (
                              <option key={v} value={v}>{m.label}</option>
                            ))}
                          </select>
                          <div className="flex gap-1">
                            <Button size="sm" variant="ghost" onClick={cancelEdit} className="h-7">
                              <X className="w-3.5 h-3.5" />
                            </Button>
                            <Button
                              size="sm"
                              disabled={saving}
                              onClick={() => saveDraft(d.id)}
                              className="h-7 bg-violet-600 hover:bg-violet-700 text-white"
                              data-testid={`boilerplate-save-${d.id}`}
                            >
                              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
                              Save
                            </Button>
                          </div>
                        </div>
                      </li>
                    );
                  }

                  return (
                    <li
                      key={d.id}
                      className="border border-slate-200 rounded-md p-3 hover:bg-slate-50"
                      data-testid={`boilerplate-draft-${d.id}`}
                    >
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <div className="min-w-0">
                          <div className="font-medium text-sm text-slate-900 truncate">{d.title}</div>
                          <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                            <Badge variant="outline" className="text-[10px]">{d.category || 'General'}</Badge>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded ${statusMeta.tone}`}>
                              {statusMeta.label}
                            </span>
                            {unfilledCount > 0 && (
                              <span className="text-[10px] text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200">
                                {unfilledCount} unfilled
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-1 flex-shrink-0">
                          <Button
                            size="sm" variant="ghost"
                            onClick={() => autofillFromClient(d)}
                            className="h-7 text-indigo-700 hover:bg-indigo-50"
                            title="Auto-fill {{company_name}}, {{cage_code}}, {{uei}}, etc. from this pursuit's client profile"
                            data-testid={`boilerplate-autofill-${d.id}`}
                          >
                            <Wand2 className="w-3.5 h-3.5 mr-1" /> Auto-fill
                          </Button>
                          <Button
                            size="sm" variant="ghost"
                            onClick={() => startEdit(d)}
                            className="h-7 text-violet-700 hover:bg-violet-50"
                            data-testid={`boilerplate-edit-${d.id}`}
                          >
                            <Edit className="w-3.5 h-3.5 mr-1" /> Edit
                          </Button>
                          <Button
                            size="sm" variant="ghost"
                            onClick={() => deleteDraft(d)}
                            className="h-7 text-slate-500 hover:text-rose-600 hover:bg-rose-50"
                            data-testid={`boilerplate-delete-${d.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                      <pre className="text-xs text-slate-600 font-mono whitespace-pre-wrap line-clamp-4 mt-1">
                        {renderBody(d.body, d.variables_filled)}
                      </pre>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
